import printLockCss from '../hiprint/css/print-lock.css?raw'
import hiprintCss from '../hiprint/css/hiprint.css?raw'

/**
 * 安全的 Base64 编码函数，支持 UTF-8 字符
 * 解决 btoa() 不支持非 Latin1 字符的问题
 */
const safeBase64Encode = (str: string): string => {
  try {
    // 方法1：使用 TextEncoder (现代浏览器)
    if (typeof TextEncoder !== 'undefined') {
      const encoder = new TextEncoder()
      const data = encoder.encode(str)

      // 将字节数组转换为二进制字符串
      let binary = ''
      for (let i = 0; i < data.length; i++) {
        binary += String.fromCharCode(data[i])
      }

      return btoa(binary)
    }

    // 方法2：手动 UTF-8 编码 (兼容旧浏览器)
    const utf8 = unescape(encodeURIComponent(str))
    return btoa(utf8)
  } catch (error) {
    console.warn('Base64 encoding failed:', error)
    throw error
  }
}

/**
 * 创建 CSS 的 data URI
 * 优先使用 Base64 编码，失败时使用 URL 编码
 * 添加标识符以便 hiprint 能够识别
 */
const createCssDataUri = (css: string, identifier: string): string => {
  try {
    // 在 data URI 中添加注释标识符，确保 hiprint 能找到
    return `data:text/css;base64,${safeBase64Encode(css)}#${identifier}`
  } catch (error) {
    console.warn('Base64 encoding failed, using charset=utf-8 fallback:', error)
    return `data:text/css;charset=utf-8,${encodeURIComponent(css)}#${identifier}`
  }
}

/**
 * 确保页面中存在打印 CSS 的 link 标签，以满足 hiprint 的检查要求
 * 这个函数会动态创建 link 标签，使用 data URI 来提供 CSS 内容
 * 主要用于支持 npm 包发布后的场景
 */
export const ensurePrintCssLinks = () => {
  const head = document.head
  let linksCreated = 0

  // 检查是否已存在 print-lock.css 链接
  if (!head.querySelector('link[media="print"][href*="print-lock"]')) {
    const printLockLink = document.createElement('link')
    printLockLink.rel = 'stylesheet'
    printLockLink.type = 'text/css'
    printLockLink.media = 'print'
    printLockLink.href = createCssDataUri(printLockCss, 'print-lock')
    // 添加标识符，方便 hiprint 检查
    printLockLink.setAttribute('data-hiprint', 'print-lock')
    head.appendChild(printLockLink)
    linksCreated++
    console.log(
      '✅ 动态创建 print-lock.css link 标签',
      printLockLink.href.substring(0, 100) + '...',
    )
  }

  // 检查是否已存在 hiprint.css 链接（可选，但保持一致性）
  if (!head.querySelector('link[media="print"][href*="hiprint"]')) {
    const hiprintLink = document.createElement('link')
    hiprintLink.rel = 'stylesheet'
    hiprintLink.type = 'text/css'
    hiprintLink.media = 'print'
    hiprintLink.href = createCssDataUri(hiprintCss, 'hiprint')
    // 添加标识符
    hiprintLink.setAttribute('data-hiprint', 'hiprint')
    head.appendChild(hiprintLink)
    linksCreated++
    console.log('✅ 动态创建 hiprint.css link 标签', hiprintLink.href.substring(0, 100) + '...')
  }

  if (linksCreated > 0) {
    console.log(`🎯 成功创建 ${linksCreated} 个打印样式 link 标签`)

    // 验证创建结果 - 等待 DOM 更新
    setTimeout(() => {
      const printLockLinks = head.querySelectorAll('link[media="print"][href*="print-lock"]')
      const hiprintLinks = head.querySelectorAll('link[media="print"][href*="hiprint"]')
      const allPrintLinks = head.querySelectorAll('link[media="print"]')

      console.log(`📋 当前页面中的 print-lock 链接数量: ${printLockLinks.length}`)
      console.log(`📋 当前页面中的 hiprint 链接数量: ${hiprintLinks.length}`)
      console.log(`📋 当前页面中的所有打印链接数量: ${allPrintLinks.length}`)

      // 显示所有打印相关的 link 标签信息
      allPrintLinks.forEach((link, index) => {
        const href = (link as HTMLLinkElement).href
        console.log(`🔗 Link ${index + 1}: ${href.substring(0, 80)}...`)
      })
    }, 100)
  }
}

/**
 * 获取完整的打印样式字符串
 * 包含 print-lock.css, hiprint.css 和可选的自定义样式
 * @param customCss 可选的自定义 CSS 样式
 * @returns 完整的样式字符串
 */
export const getPrintStyles = (customCss: string = '') => {
  return `<style type="text/css">
    ${printLockCss}
    ${hiprintCss}
    ${customCss}
  </style>`
}

/**
 * 在调用 print2 之前调用此函数，确保 hiprint 能找到必要的 link 标签
 * 这是为了解决 hiprint.bundle.js 中的检查逻辑
 */
export const preparePrintEnvironment = () => {
  ensurePrintCssLinks()
}

// 导出 CSS 内容，供需要时使用
export { printLockCss, hiprintCss }
